/**
 * useStoreUpdate — decides whether to nudge the user toward the App Store / Play Store.
 *
 * NATIVE ONLY. Web builds return `{ tier: 'none' }` unconditionally — there is no store to
 * point at from the browser. Distinct from `useUpdateAvailable`, which drives the OTA banner
 * against Trade Loop's own R2 release manifest and force-applies JS bundles silently. This
 * hook drives the store banner/modal for updates that require the user to leave the app.
 *
 * Event-driven, NOT polled: once on mount, and again whenever the app returns to the
 * foreground (`appStateChange` with `isActive: true`). Same shape as `useUpdateAvailable`.
 * An interval would be dead weight — a foregrounded timer duplicates the resume trigger, and
 * a backgrounded one is suspended by the WebView anyway.
 *
 * A dismissal is scoped to the manifest's `latestVersion`: bumping the manifest re-shows the
 * banner. The `mandatory` tier ignores dismissal — the modal is the only exit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { STORE_UPDATE_MANIFEST_URL } from '@/config/store-update-manifest';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/config/app-store-links';
import { isNative, platform } from '@/utils/platform';
import {
  isStoreUpdateManifest,
  resolveTier,
  StoreUpdateManifest,
  StoreUpdatePlatform,
  StoreUpdatePlatformPolicy,
  StoreUpdateTier
} from '@/types/store-update';

const DISMISSED_KEY = 'tradeloop.storeUpdate.dismissedVersion';
const FETCH_TIMEOUT_MS = 5000;

/**
 * Dev-only preview override.
 *
 * `?storeUpdatePreview=optional|recommended|mandatory` on the URL forces the hook to synthesise
 * the corresponding tier so the banner + modal can be visually verified in a browser without a
 * native build.
 *
 * Gated on `import.meta.env.DEV` so the entire block tree-shakes out of production bundles.
 * Read once at hook-init so rules-of-hooks are respected even if the query string changes
 * mid-session.
 */
type PreviewTier = Exclude<StoreUpdateTier, 'none'>;

function readPreviewTier(): PreviewTier | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('storeUpdatePreview');
  if (raw === 'optional' || raw === 'recommended' || raw === 'mandatory') return raw;
  return null;
}

/** localStorage throws in private-mode webviews; a UI nudge must never break the app. */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export interface UseStoreUpdateResult {
  /** Which tier of nudge to render, or 'none' for silent. */
  tier: StoreUpdateTier;
  /** URL to open when the user taps the CTA. Undefined when no policy applies. */
  storeUrl: string | undefined;
  /** Latest version from the manifest, for display. Undefined until manifest resolves. */
  latestVersion: string | undefined;
  /** Running native version reported by `App.getInfo()`. Undefined on web or before it resolves. */
  runningVersion: string | undefined;
  /** Hides the banner for the current `latestVersion`. No-op for mandatory tier. */
  dismiss: () => void;
}

async function fetchManifest(): Promise<StoreUpdateManifest | null> {
  try {
    const response = await fetch(STORE_UPDATE_MANIFEST_URL, {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      headers: { accept: 'application/json' }
    });
    if (!response.ok) return null;
    const parsed = await response.json();
    if (!isStoreUpdateManifest(parsed)) return null;
    return parsed;
  } catch {
    // Offline, DNS, timeout, malformed JSON — all silent. A missing manifest means no nudge,
    // not an error. Same failure model as `useUpdateAvailable`.
    return null;
  }
}

export function useStoreUpdate(): UseStoreUpdateResult {
  // Read once. If a preview tier is requested, the returned state below overrides everything.
  const [previewTier] = useState<PreviewTier | null>(() => readPreviewTier());
  // Preview-only dismissal state. Real dismissal is keyed to `latestVersion` in localStorage;
  // preview has no manifest, so it lives entirely in component state and resets on reload.
  const [previewDismissed, setPreviewDismissed] = useState(false);

  const [manifest, setManifest] = useState<StoreUpdateManifest | null>(null);
  const [runningVersion, setRunningVersion] = useState<string | undefined>(undefined);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(() => safeGet(DISMISSED_KEY));

  // Prevents an in-flight fetch from clobbering a newer one on rapid resume cycles.
  const fetchIdRef = useRef(0);

  // Read the running NATIVE version. Not the web bundle's — the manifest describes what the
  // store has, so the comparison must be against the installed binary.
  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;

    App.getInfo()
      .then((info) => {
        if (cancelled) return;
        setRunningVersion(info.version);
      })
      .catch(() => {
        /* No app info means no reliable comparison — stay silent. */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!isNative) return;
    const requestId = ++fetchIdRef.current;
    const next = await fetchManifest();
    // Only apply if we are still the latest fetch — a resume that fired mid-flight has priority.
    if (requestId !== fetchIdRef.current) return;
    // Preserve the previous manifest on a transient failure rather than flapping the banner off.
    if (next) setManifest(next);
  }, []);

  // Check on mount, then whenever the app comes back to the foreground.
  useEffect(() => {
    if (!isNative) return;

    void refresh();

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void refresh();
    });

    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [refresh]);

  const policy: StoreUpdatePlatformPolicy | undefined = manifest
    ? manifest[platform as StoreUpdatePlatform]
    : undefined;

  const rawTier = resolveTier(runningVersion, policy);
  const dismissedThisVersion = Boolean(
    policy && dismissedVersion && dismissedVersion === policy.latestVersion
  );

  // A dismissal downgrades 'optional' to 'none' — but never touches 'recommended' or
  // 'mandatory'. Recommended is sticky by definition, and mandatory has no dismiss control.
  const tier: StoreUpdateTier = rawTier === 'optional' && dismissedThisVersion ? 'none' : rawTier;

  const dismiss = useCallback(() => {
    if (!policy) return;
    if (rawTier === 'mandatory' || rawTier === 'recommended') return;
    safeSet(DISMISSED_KEY, policy.latestVersion);
    setDismissedVersion(policy.latestVersion);
  }, [policy, rawTier]);

  // Dev-only preview override. Runs LAST so all normal state hooks have executed and rules-of-
  // hooks are respected. Non-dev builds tree-shake this branch out entirely.
  if (import.meta.env.DEV && previewTier) {
    // Only the 'optional' tier honours dismissal — matches production semantics where sticky
    // and mandatory tiers ignore the close controls entirely.
    const effectiveTier: StoreUpdateTier =
      previewTier === 'optional' && previewDismissed ? 'none' : previewTier;
    return {
      tier: effectiveTier,
      storeUrl: platform === 'android' ? PLAY_STORE_URL : APP_STORE_URL,
      latestVersion: '1.2.3',
      runningVersion: runningVersion ?? '1.0.0',
      dismiss: () => setPreviewDismissed(true)
    };
  }

  return {
    tier,
    storeUrl: policy?.storeUrl,
    latestVersion: policy?.latestVersion,
    runningVersion,
    dismiss
  };
}
